import { LENS_COLOR } from '@/app/_lens-components/constants';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import type { ColorItem } from '@/app/_lens-components/types';
import Image from '@/components/Image';
import Tooltip from '@/components/Tooltip';
import type { MouseEvent } from 'react';

type TooltipMode = 'none' | 'hover' | 'click';

interface ColorOptionListProps {
  colorList?: ColorItem[] | null;
  isSelected: (colorItem: ColorItem) => boolean;
  onColorClick: (colorItem: ColorItem) => void;
  className?: string;
  itemClassName?: string;
  imageSize?: number;
  tooltipMode?: TooltipMode;
  getItemDataAttributes?: (colorItem: ColorItem) => Record<string, string | number | undefined>;
}

export default function ColorOptionList({
  colorList,
  isSelected,
  onColorClick,
  className = '',
  itemClassName = '',
  imageSize = 16,
  tooltipMode = 'hover',
  getItemDataAttributes,
}: ColorOptionListProps) {
  if (!Array.isArray(colorList) || !colorList.length) {
    return null;
  }

  const renderColorImage = (colorItem: ColorItem) => (
    <Image
      src={colorItem.option_img}
      alt={colorItem.color_name}
      width={imageSize}
      height={imageSize}
    />
  );

  const handleColorClick = (event: MouseEvent, colorItem: ColorItem) => {
    event.stopPropagation();
    onColorClick(colorItem);
  };

  return (
    <ul className={`${stepCommonClassName.colorSelector} ${className}`}>
      {colorList.map((colorItem, index) => (
        <li
          key={`${colorItem.lens_color}-${index}`}
          data-lens-color={colorItem.lens_color}
          {...getItemDataAttributes?.(colorItem)}
          className={`${stepCommonClassName.colorItem} ${
            isSelected(colorItem)
              ? stepCommonClassName.selectedColorItem
              : stepCommonClassName.unselectedColorItem
          } ${itemClassName}`}
          style={colorItem.lens_color === LENS_COLOR.NON ? { display: 'none' } : {}}
          onClick={(event) => handleColorClick(event, colorItem)}
        >
          {tooltipMode === 'none' ? (
            renderColorImage(colorItem)
          ) : (
            <Tooltip
              title={colorItem.explain || colorItem.color_name}
              trigger={tooltipMode === 'click' ? 'click' : 'hover'}
              arrow={tooltipMode === 'click'}
              placement="top"
              variant="light"
              stopPropagation={false}
              sx={{
                zIndex: 1500,
              }}
            >
              {renderColorImage(colorItem)}
            </Tooltip>
          )}
        </li>
      ))}
    </ul>
  );
}
